/**
 * Polls API Service (ported from flock-expo/src/services/polls.service.ts)
 */

import { api } from '@/lib/apiClient';
import type { Poll, PollOption, CreatePollPayload } from '@flock-types/polls.types';

/**
 * Pin a request to a specific organization (cross-org group rail). The backend
 * verifies membership of the org named in the header, so this is safe for any
 * org the user belongs to. Omitted → apiClient falls back to the current org.
 */
function orgConfig(organizationId?: string) {
  return organizationId ? { headers: { 'X-Organization-Id': organizationId } } : undefined;
}

interface PollsListResponse {
  success: boolean;
  data: Poll[];
}

interface PollResponse {
  success: boolean;
  data: Poll;
}

interface PollOptionResponse {
  success: boolean;
  data: PollOption;
}

interface MutationResponse {
  success: boolean;
  message?: string;
}

export type PollUpdates = Partial<
  Pick<
    Poll,
    | 'question'
    | 'description'
    | 'status'
    | 'allow_multiple_choices'
    | 'is_anonymous'
    | 'show_results_before_close'
    | 'closes_at'
  >
>;

export const pollsService = {
  /** Get all polls for a group */
  async getPolls(
    groupId: string,
    status: 'active' | 'closed' | 'archived' = 'active',
    organizationId?: string
  ): Promise<Poll[]> {
    const response = await api.get<PollsListResponse>(
      `/groups/${groupId}/polls?status=${status}`,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Get a single poll with all options and vote data */
  async getPollDetail(pollId: string, organizationId?: string): Promise<Poll> {
    const response = await api.get<PollResponse>(`/polls/${pollId}`, orgConfig(organizationId));
    return response.data;
  },

  /** Create a new poll */
  async createPoll(
    groupId: string,
    payload: CreatePollPayload,
    organizationId?: string
  ): Promise<Poll> {
    const response = await api.post<PollResponse>(
      `/groups/${groupId}/polls`,
      payload,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Update a poll (question, description, status, settings) */
  async updatePoll(pollId: string, updates: PollUpdates, organizationId?: string): Promise<Poll> {
    const response = await api.put<PollResponse>(
      `/polls/${pollId}`,
      updates,
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Delete a poll */
  async deletePoll(pollId: string, organizationId?: string): Promise<void> {
    await api.delete<MutationResponse>(`/polls/${pollId}`, orgConfig(organizationId));
  },

  /** Add an option to a poll */
  async addOption(pollId: string, label: string, organizationId?: string): Promise<PollOption> {
    const response = await api.post<PollOptionResponse>(
      `/polls/${pollId}/options`,
      { label },
      orgConfig(organizationId)
    );
    return response.data;
  },

  /** Delete an option from a poll */
  async deleteOption(optionId: string, organizationId?: string): Promise<void> {
    await api.delete<MutationResponse>(`/poll-options/${optionId}`, orgConfig(organizationId));
  },

  /** Vote on a poll (one or more options) */
  async vote(pollId: string, optionIds: string[], organizationId?: string): Promise<void> {
    await api.post<MutationResponse>(
      `/polls/${pollId}/vote`,
      { optionIds },
      orgConfig(organizationId)
    );
  },

  /** Retract vote from a poll */
  async retractVote(pollId: string, organizationId?: string): Promise<void> {
    await api.delete<MutationResponse>(`/polls/${pollId}/vote`, orgConfig(organizationId));
  },
};
